import { Whitelabel } from "@/common/common";
import { DCEvent, EventDispatcher } from "@/events/eventsDomain";
import { prepI18nState, PreppedI18nState } from "@/i18n/i18nDomain";
import { Language } from "@/i18n/languages";
import {
  ChangeEvent,
  ClipboardEvent,
  createContext,
  EventHandler,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  MouseEventHandler,
  SyntheticEvent,
  useContext,
  useMemo,
} from "react";
import { RootStore } from "./RootStore";
import ShareholdersStore from "./ShareholdersStore";
import { UserPrefs } from "./userPrefsDomain";
import UserStore from "./UserStore";

export const RootStoreContext = createContext({} as RootStore);

export const useRootStore = (): RootStore => useContext(RootStoreContext);
export const useI18n = (overrideLang?: Language): PreppedI18nState => {
  const state = useRootStore().i18n;
  return useMemo<PreppedI18nState>(() => {
    return prepI18nState(state, overrideLang);
  }, [state, overrideLang]);
};
export const useUserStore = (): UserStore => useRootStore().userStore;
export const useShareholdersStore = (): ShareholdersStore => useRootStore().shareholdersStore;
export const useMessageStore = () => useRootStore().messageStore;
export const usePortfolioStore = () => useRootStore().portfolioStore;
export const useDialogStore = () => useRootStore().dialogStore;
export const useTranslation = (lang?: Language) => useI18n(lang);
export const useLanguage = () => useI18n().lang;
export const useLocaleFormat = (lang?: Language) => useI18n(lang).f;
export const useUserPrefs = (): UserPrefs => useRootStore().userPrefs ?? {};
export const useWhitelabel = (): Whitelabel | undefined =>
  useRootStore().userStore.companyInfo?.company.whitelabel;

export const DispatchContext = createContext<EventDispatcher>(() => {
  throw new Error("DispatchContext not initialized");
});
export const useDispatch = (): EventDispatcher => useContext(DispatchContext);

export type DCDomEventHandler<E extends SyntheticEvent<T>, T = Element> =
  | ((event: E) => void | DCEvent[] | unknown)
  | DCEvent[];

export type DCMouseEventHandler<T = Element> = DCDomEventHandler<MouseEvent<T>, T>;
export type DCFocusEventHandler<T = Element> = DCDomEventHandler<FocusEvent<T>, T>;
export type DCChangeEventHandler<T = Element> = DCDomEventHandler<ChangeEvent<T>, T>;
export type DCKeyboardEventHandler<T = Element> = DCDomEventHandler<KeyboardEvent<T>, T>;
export type DCClipboardEventHandler<T = Element> = DCDomEventHandler<ClipboardEvent<T>, T>;

export type DCCallback = DCEvent[] | (() => void | DCEvent[] | unknown);

export const useDomEventHandler = <E extends SyntheticEvent<T>, T = Element>(
  handler?: DCDomEventHandler<E, T>,
): EventHandler<E> | undefined => {
  const dispatch = useDispatch();
  return !handler
    ? undefined
    : (event: E) => {
        if (Array.isArray(handler)) {
          dispatch(handler);
        } else {
          const retVal = handler(event);
          if (Array.isArray(retVal)) {
            dispatch(retVal);
          }
        }
      };
};

export const useFocusEventHandler = <T = Element>(handler?: DCFocusEventHandler<T>) =>
  useDomEventHandler<FocusEvent<T>, T>(handler);

export const useChangeEventHandler = <T = Element>(handler?: DCChangeEventHandler<T>) =>
  useDomEventHandler<ChangeEvent<T>, T>(handler);

export const useKeyboardEventHandler = <T = Element>(handler?: DCKeyboardEventHandler<T>) =>
  useDomEventHandler<KeyboardEvent<T>, T>(handler);

export const useClipboardEventHandler = <T = Element>(handler?: DCClipboardEventHandler<T>) =>
  useDomEventHandler<ClipboardEvent<T>, T>(handler);

export const mouseEventHandler = <T = Element>(
  dispatch: EventDispatcher,
  handler?: DCMouseEventHandler<T>,
): MouseEventHandler<T> | undefined =>
  !handler
    ? undefined
    : (event: MouseEvent<T>) => {
        event.preventDefault();
        event.stopPropagation();
        if (Array.isArray(handler)) {
          dispatch(handler);
        } else {
          const retVal = handler(event);
          if (Array.isArray(retVal)) {
            dispatch(retVal);
          }
        }
      };

export const useMouseEventHandler = <T = Element>(
  handler?: DCMouseEventHandler<T>,
): MouseEventHandler<T> | undefined => {
  const dispatch = useDispatch();
  return mouseEventHandler(dispatch, handler);
};

export const useCallbackHandler = (handler?: DCCallback) => {
  const dispatch = useDispatch();
  if (!handler) {
    return undefined;
  } else if (Array.isArray(handler)) {
    return () => {
      dispatch(handler);
    };
  } else {
    return () => {
      const result = handler();
      if (Array.isArray(result)) {
        dispatch(result);
      }
    };
  }
};
